import { Grid, LinearProgress, Modal, Stack } from "@mui/material";
import React from "react";
import {
  StyledCancelButton,
  StyledDivider,
  StyledModalContent,
  StyledModalTitle,
  StyledModalWarningText,
  StyledTitle,
} from "../styles/TableStyledComponents";
import StyledButton from "src/components/dashboard/StyledButton";
import { DcaStrategyDto } from "src/store/features/api/generated";
import api from "src/store/features/api";
import { formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";
import { dateFormatter } from "src/utils/helpers/DateAndTimeHelpers";
import { useDcaSlice } from "src/store/features/dca.slice";
import { trackEvent } from "src/Amplitude";
import { useAppDispatch } from "src/store/store";

export default function CancelModal({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [deleteStrategy, { isLoading }] = api.useCancelDcaStrategyMutation();
  const { selectedStrategy: strategy } = useDcaSlice();
  const dispatch = useAppDispatch();

  const handleForceRefetch = () => {
    dispatch(api.util.invalidateTags(["getDcaStrategiesTag"]));
  };

  const handleCancelStrategy = async (id: string) => {
    trackEvent("Clicked on accept cancel plan", { id }, "DCA");
    await deleteStrategy({
      id,
    })
      .then(() => {
        trackEvent("Cancelled DCA", { id }, "DCA");
        setOpen(false);
        handleForceRefetch();
      })
      .catch(() => {
        window.alert("Error cancelling strategy, please contact support");
      });
    setOpen(false);
  };

  if (!strategy) return null;

  const {
    _id,
    amount: buyValue,
    buyCurrency: { code: buyToken },
    sellCurrency: { code: sellToken },
    frequency,
    startDate,
  } = strategy as DcaStrategyDto;

  const content = [
    {
      label: "DCA",
      value: `${formatCurrency(
        buyValue,
        sellToken,
      )} of ${buyToken} every ${frequency.toLocaleLowerCase()} starting ${dateFormatter(
        startDate,
      )}`,
    },
  ];

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <StyledModalContent elevation={2}>
        <StyledModalTitle>Cancel Scheduled order strategy</StyledModalTitle>
        <StyledDivider />
        <StyledModalWarningText>
          Please confirm that you want to cancel the following plan:
        </StyledModalWarningText>
        <Grid container spacing={"16px"} mt={"2px"}>
          {content.map(({ label, value }) => (
            <>
              <Grid item xs={3}>
                <StyledTitle>{label}</StyledTitle>
              </Grid>
              <Grid item xs={9}>
                <StyledTitle>{value}</StyledTitle>
              </Grid>
            </>
          ))}
        </Grid>
        <StyledTitle sx={{ fontSize: "12px", marginTop: "16px" }}>
          Note: By proceeding, you acknowledge that any cancellations made cannot be undone
        </StyledTitle>
        <Stack direction="row" gap={2} mt={"20px"}>
          <StyledCancelButton variant="outlined" fullWidth onClick={() => setOpen(false)}>
            Back
          </StyledCancelButton>
          <StyledButton disabled={isLoading} onClick={() => handleCancelStrategy(_id)}>
            Cancel Plan
          </StyledButton>
          {isLoading && <LinearProgress />}
        </Stack>
      </StyledModalContent>
    </Modal>
  );
}
